<template>
  <div>
    <div
      class="font-primary font-medium text-[16px] leading-[18px] text-gray-800 mb-5"
    >
      {{ paymentMethod?.last4 ? 'Your' : 'Enter' }} payment details
    </div>

    <div
      v-if="paymentMethod?.last4"
      class="mb-6 flex items-center justify-between font-primary text-[14px]"
    >
      <div class="hidden mobile-l:block mr-3">Payment method</div>

      <div
        class="flex items-center whitespace-nowrap w-full mobile-l:w-auto justify-between mobile-l:justify-normal"
      >
        <div class="flex items-center">
          <CreditCard :type="paymentMethod.type" class="mr-2.5" />
          <div>
            &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull;
            &bull;&bull;&bull;&bull; {{ paymentMethod.last4 }}
          </div>
        </div>
        <div
          class="w-fit text-green-700 cursor-pointer ml-[15px] font-medium"
          :class="cardClasses"
          @click="emit('updatePaymentMethod')"
        >
          Change
        </div>
      </div>
    </div>

    <div v-else class="mb-6" :class="cardClasses">
      <div id="card" />
    </div>

    <Message v-if="errorMessage" type="error" class="mb-6">
      {{ errorMessage }}
    </Message>

    <div
      class="font-secondary text-[12px] leading-[18px] tracking-[0.3px] text-gray-800 mb-4"
    >
      You'll be charged
      <span v-for="(price, index) in selectedPlanItem?.prices" :key="index">
        <span>{{ getBillingCyclePrice(price) }}</span>
        <span v-if="hasMoreThanOnePrice">
          <span v-if="!index"> per {{ selectedBillingCycle.type }} plus </span>
          <span v-else> per copyright</span>
        </span>
        <span v-else> per {{ selectedBillingCycle.type }} </span>
      </span>
      until you cancel your subscription. Previous charges won't be refunded
      when you cancel unless it's legally required. Any unpaid balances will
      accrue until paid. Your payment data is encrypted and secure. All amounts
      shown are in USD.
    </div>

    <div
      class="w-full font-secondary text-[12px] leading-[18px] tracking-[0.3px] text-gray-800 inline-block mb-7 align-bottom"
    >
      <Checkbox
        :modelValue="acceptTerms"
        @update:modelValue="emit('update:acceptTerms', $event)"
        :size="20"
        :disabled="loading"
        class="items-start! border-gray-300!"
      >
        <div class="text-[12px] w-full">
          I agree to the Dacr
          <a
            target="_blank"
            href="/legal/terms"
            class="text-link -mr-1"
            @click.stop
            >Terms</a
          >,
          <a
            target="_blank"
            class="text-link -mr-1"
            href="/legal/privacy"
            @click.stop
            >Privacy Policy</a
          >, and
          <span class="inline-flex">
            <a
              target="_blank"
              class="text-link"
              href="/legal/pricing"
              @click.stop
              >Pricing Terms</a
            >.
          </span>
        </div>
      </Checkbox>
    </div>

    <UiButton
      class="w-full!"
      :disabled="!acceptTerms"
      :loading="loading"
      primary
      @click="emit('purchase')"
    >
      Purchase
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import CreditCard from '@/features/billing/components/CreditCard.vue'
import type { PlanItem, PlanPriceItem } from '@/shared/graphql/legacy/generated'
import { Checkbox, UiButton, Message } from '@dacr/ui'

defineProps<{
  paymentMethod?: { last4?: string; type: string } | null
  cardClasses: Record<string, boolean>
  acceptTerms: boolean
  loading: boolean
  errorMessage: string
  selectedPlanItem?: PlanItem
  selectedBillingCycle: { type: string; description: string }
  hasMoreThanOnePrice: boolean
  getBillingCyclePrice: (price: PlanPriceItem) => string
}>()

const emit = defineEmits<{
  (e: 'update:acceptTerms', value: boolean): void
  (e: 'updatePaymentMethod'): void
  (e: 'purchase'): void
}>()
</script>
